// ProductDetail v3 (PV new layout since 2026)
import {
  Body,
  Breadcrumbs,
  Card,
  Container,
  Flex,
  styled,
} from '@teko/ds-desktop';
import { WrenchFilled } from '@teko/ds-icon';
import {
  BlockHeader,
  NoProductsFound,
  ProductCarousel,
} from '@teko/teko-ecommerce';
import { InlineBanner } from 'components/InlineBanner';
import { LazyComponent } from 'components/LazyComponent';
import { NotFoundProductLayout } from 'components/ProductDetail/styles';
import { INLINE_BANNER_CLASSNAME } from 'constants/smartBanner';
import { useProductDetailState, useTenantConfig } from 'contexts';
import { isServerless } from 'helpers';
import { scrollSmoothToIdWithOffset } from 'helpers/page-actions';
import { t } from 'i18n';
import { BreadcrumbSchema } from 'modules/product-detail/shared/BreadcrumbSchema';
import { FlashSalePromotionBlock } from 'modules/product-detail/shared/FlashSalePromotionBlock';
import { MetaData } from 'modules/product-detail/shared/MetaData';
import {
  BottomActionBar as ActionButtons,
  NewPolicies,
  OutstandingFeatures,
  ProductInfo,
  ProductPrice,
  ProductPromotions,
  RelatedPosts,
  RelatedVideos,
} from 'modules/product-detail/shared/NewLayoutComponents';
import { ReviewSection } from 'modules/product-detail/shared/ProductReview/ReviewSection';
import dynamic from 'next/dynamic';
import { Box } from 'reflexbox';
import { ProductDetailPromotion } from 'types/promotions';
import { NewProductImages, ShareAndWishlist } from './new-components';

const FAQSection = dynamic(() =>
  import('components/FAQ').then((mod) => mod.FAQSection),
);

const ConfirmOver18YearOldModal = dynamic(() =>
  import('../../shared/ConfirmOver18YearOldModal').then(
    (mod) => mod.ConfirmOver18YearOldModal,
  ),
);

const ProductCombo = dynamic(() =>
  import('./ProductCombo').then((mod) => mod.ProductCombo),
);

const PaymentPromotion = dynamic(() =>
  import('../../shared/PaymentPromotion').then((mod) => mod.PaymentPromotion),
);

const ProductVariants = dynamic(() =>
  import('../../shared/ProductVariants').then((mod) => mod.ProductVariants),
);

const RelatedPromotions = dynamic(() =>
  import('./new-components').then((mod) => mod.RelatedPromotions),
);

const NewProductDescription = dynamic(() =>
  import('./NewProductDescription').then((mod) => mod.NewProductDescription),
);

interface DetailPageProps {
  showRelatedPromotion?: boolean;
  extraPromotions?: ProductDetailPromotion[];
}

export const NewDetailPage: React.FC<DetailPageProps> = (props) => {
  const {
    product,
    breadcrumbs,
    inlineBanners,
    showPopupCheckAge,
    setShowPopupCheckAge,
    relatedProducts,
  } = useProductDetailState();
  const { epicOmni1550 } = useTenantConfig();
  const { showRelatedPromotion = false, extraPromotions } = props;

  if (!product) {
    return (
      <NotFoundProductLayout>
        <NoProductsFound show text={t('Không tìm thấy sản phẩm')} />
      </NotFoundProductLayout>
    );
  }

  const arrowStyle = {
    backgroundColor: 'white',
    borderRadius: 999,
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 5px 15px',
  };

  /**
   * 690: max left container width
   * 1200: max container width
   * 16 * 2: padding left and right
   */
  const leftContainerWidth = (690 / (1200 - 16 * 2)) * 100;

  const handleClickCompulsoryProduct = () => {
    scrollSmoothToIdWithOffset('combo-sections');
  };

  return (
    <>
      <BreadcrumbSchema />
      <MetaData />
      <Container marginBottom="s16">
        <Breadcrumbs breadcrumbs={breadcrumbs || []} />
        <Card padding="s16">
          <Flex align="start" gap="s24">
            <Box
              width={`${leftContainerWidth}%`}
              style={{ position: 'sticky', top: 114 }}
            >
              <NewProductImages />
              <ShareAndWishlist />
              {!!product.seoInfo?.shortDescription?.length && (
                <OutstandingFeatures
                  onViewDetail={() =>
                    scrollSmoothToIdWithOffset('desktop-product-description')
                  }
                  features={product.seoInfo?.shortDescription}
                />
              )}
              <ProductCombo />
              <NewPolicies />
            </Box>
            <Box
              width={`calc(100% - ${leftContainerWidth}% - 24px)`}
              style={{ position: 'sticky', top: 114 }}
            >
              {product.hasCompulsoryProducts && (
                <StyledCompulsoryProductIndicator
                  onClick={handleClickCompulsoryProduct}
                >
                  <WrenchFilled size={16} />
                  <Body>
                    {t('Chỉ bán theo combo - Không bán lẻ. Click để XEM COMBO')}
                  </Body>
                </StyledCompulsoryProductIndicator>
              )}
              <FlashSalePromotionBlock />
              <Card marginBottom="s12">
                <ProductInfo />
                <ProductVariants />
                {!!product.status?.sellable && <ProductPrice />}
                <InlineBanner
                  inlineBanners={inlineBanners}
                  inlineBannerClass={
                    INLINE_BANNER_CLASSNAME.INLINE_PDP_RELATEDPROMO
                  }
                />
              </Card>
              <ProductPromotions />
              <ActionButtons
                hasCompulsoryProduct={product.hasCompulsoryProducts}
              />
              {showRelatedPromotion && (
                <LazyComponent>
                  <RelatedPromotions extraPromotions={extraPromotions} />
                </LazyComponent>
              )}
              <LazyComponent>
                <PaymentPromotion />
              </LazyComponent>
            </Box>
          </Flex>
        </Card>
        <LazyComponent>
          <Flex gap="s16" marginTop="s16" align="start">
            <Box width="730px">
              <Card>
                <NewProductDescription
                  tabTitle={[t('Thông số kỹ thuật'), t('Chi tiết sản phẩm')]}
                  buttonType="ghost"
                />
              </Card>
            </Box>
            <Box width="calc(100% - 730px - 16px)">
              <Flex direction="column" gap="s8">
                <RelatedPosts />
                <RelatedVideos />
              </Flex>
            </Box>
          </Flex>
        </LazyComponent>
        {epicOmni1550 && (
          <LazyComponent>
            <Card marginTop="s16">
              <ReviewSection />
            </Card>
          </LazyComponent>
        )}
        {!!product.seoInfo?.faq?.length && (
          <LazyComponent>
            <Card marginTop="s16">
              <FAQSection
                faq={product.seoInfo.faq}
                style={{ padding: '0px' }}
              />
            </Card>
          </LazyComponent>
        )}
        {relatedProducts.length ? (
          <LazyComponent>
            <Card marginTop="s16">
              <BlockHeader title={t('Sản phẩm liên quan')} />
              <ProductCarousel
                products={relatedProducts}
                productsPerRow={5}
                regionName="relatedProducts"
                virtual={isServerless()}
                customStyleLeftArrow={arrowStyle}
                customStyleRightArrow={arrowStyle}
              />
            </Card>
          </LazyComponent>
        ) : null}
      </Container>

      {showPopupCheckAge && (
        <ConfirmOver18YearOldModal
          showPopupCheckAge={showPopupCheckAge}
          setShowPopupCheckAge={setShowPopupCheckAge}
          okText={t('Tôi trên 18 tuổi')}
        />
      )}
    </>
  );
};

const StyledCompulsoryProductIndicator = styled.div`
  background: linear-gradient(94deg, #1435c3 3.27%, #5345fa 100%);
  padding: 4px 8px;
  border-radius: 44px;
  width: 100%;
  color: white;
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
  cursor: pointer;
  text-align: center;
`;
